import PiwikPro, {
  CustomEvent,
  ContentTracking,
  PageViews,
} from '@piwikpro/react-piwik-pro';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { useInspirationEditorStore } from '$/pages/InspirationEditorPage/stores/useInspirationEditorStore';
import { type TypedToOptions } from '$/routes';
import { queryClient } from '$/services/client';
import {
  projectFoldersQuery,
  projectsQuery,
  inspirationProjectQuery,
} from '$/services/usecases/projects/queries';
import {
  schoolClassesQuery,
  studentsQuery,
} from '$/services/usecases/school/queries';
import {
  DataLayerVariableOptions,
  mapPathnameToPage,
  parseContentPiece,
  parseContentTarget,
  setDataLayerValues,
  setIsInAppWebView,
  type PiwikContentInteraction,
  type PiwikContentName,
  type PiwikContentPiece,
  type PiwikContentTarget,
  type PiwikEventAction,
  type PiwikEventCategory,
} from '$/utils/piwikUtils';

export const setPageViewInformation = async (
  pathname: string,
  search: Record<string, unknown>,
) => {
  const { dimensions, pageTitle } = mapPathnameToPage(
    pathname as TypedToOptions,
    search,
  );

  document.title = `SPECTRUM | ${pageTitle}`;

  const { user } = useAuthenticationStore.getState();

  if (!user) {
    setDataLayerValues(dimensions, pathname, document.title);
    return;
  }

  const needsProjects = dimensions.includes(DataLayerVariableOptions.Projects);
  const needsClasses = dimensions.includes(DataLayerVariableOptions.Classes);
  const needsStudents = dimensions.includes(DataLayerVariableOptions.Students);
  const needsProjectFolders = dimensions.includes(
    DataLayerVariableOptions.Projectfolders,
  );
  const needsProjectInspirationId =
    dimensions.includes(DataLayerVariableOptions.InspirationImage) &&
    pathname.includes('/inspiration-project/');

  const queries = await Promise.allSettled([
    needsProjects
      ? queryClient.ensureQueryData(projectsQuery())
      : Promise.resolve(null),
    needsClasses
      ? queryClient.ensureQueryData(schoolClassesQuery())
      : Promise.resolve(null),
    needsStudents
      ? queryClient.ensureQueryData(studentsQuery)
      : Promise.resolve(null),
    needsProjectFolders
      ? queryClient.ensureQueryData(projectFoldersQuery())
      : Promise.resolve(null),
    needsProjectInspirationId
      ? queryClient.ensureQueryData(
          inspirationProjectQuery(
            pathname.replace('/inspiration-project/', ''),
          ),
        )
      : Promise.resolve(null),
  ]);

  const projectCount =
    queries[0].status === 'fulfilled'
      ? (queries[0].value?.response?.payload?.length ?? null)
      : null;
  const classesCount =
    queries[1].status === 'fulfilled'
      ? (queries[1].value?.response?.length ?? null)
      : null;
  const studentsCount =
    queries[2].status === 'fulfilled'
      ? (queries[2].value?.response?.length ?? null)
      : null;
  const projectFoldersCount =
    queries[3].status === 'fulfilled'
      ? (queries[3].value?.response?.payload?.length ?? null)
      : null;
  const projectInspirationId =
    queries[4].status === 'fulfilled'
      ? (queries[4].value?.response?.payload?.inspirationId ?? null)
      : null;
  setDataLayerValues(
    dimensions,
    pathname,
    document.title,
    projectCount,
    classesCount,
    studentsCount,
    projectFoldersCount,
    projectInspirationId,
  );
};

export const trackDesignGuideCustomPageView = (
  scene: 'exterior' | 'interior',
) => {
  const pageTitle = `SPECTRUM | DesignGuide-${scene === 'exterior' ? 'Exterior' : 'Interior'}`;
  document.title = pageTitle;
  PageViews.trackPageView(pageTitle);
};

export const determineInspirationEditorEventCategory =
  (): PiwikEventCategory => {
    const { initialHarmonyIds } = useInspirationEditorStore.getState();
    if (initialHarmonyIds && initialHarmonyIds.length > 0) {
      return 'Tools-DesignGuide';
    }
    return 'Tools-Inspiration';
  };

export const trackEvent = (
  category: PiwikEventCategory,
  action: PiwikEventAction,
  name?: string,
  value?: number,
) => {
  CustomEvent.trackEvent(category, action, name, value);
};

export const trackContentImpression = (
  contentName: PiwikContentName,
  contentPiece: PiwikContentPiece,
  contentTarget: PiwikContentTarget,
) => {
  ContentTracking.trackContentImpression(
    contentName,
    parseContentPiece(contentPiece),
    parseContentTarget(contentTarget),
  );
};

export const trackContentInteraction = (
  contentInteraction: PiwikContentInteraction,
  contentName: PiwikContentName,
  contentPiece: PiwikContentPiece,
  contentTarget: PiwikContentTarget,
) => {
  ContentTracking.trackContentInteraction(
    contentInteraction,
    contentName,
    parseContentPiece(contentPiece),
    parseContentTarget(contentTarget),
  );
};

export const logError = (error: unknown) => {
  console.error(error);
};

export const enablePiwik = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const piwikContainerId = searchParams.get('piwikContainerId');
  const piwikContainerUrl = searchParams.get('piwikContainerUrl');

  const id = piwikContainerId ?? import.meta.env.VITE_PIWIK_CONTAINER_ID;
  const url = piwikContainerUrl ?? import.meta.env.VITE_PIWIK_CONTAINER_URL;

  setIsInAppWebView(
    piwikContainerId != null &&
      piwikContainerId !== import.meta.env.VITE_PIWIK_CONTAINER_ID,
  );

  if (id == null || url == null) {
    console.warn('Visiting not recorded');
    return;
  }

  PiwikPro.initialize(id, url);
};
